/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action, StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './NavigationView.types.tsx';
import { updateContainerState } from './updateContainerState.tsx';
import { mergeViews } from './mergeViews.tsx';

const debounceInterval = 2000; // 2 seconds
const throttleInterval = 12000; // 12 seconds ~ 5 times in min

const refreshViewsFunc = async (
	{ setState, getState, dispatch }: StoreActionApi<State>,
	{ navigationRemote, cloudId, containerId, onFailure, onSuccess }: Props,
) => {
	if (
		!cloudId ||
		!containerId ||
		!navigationRemote ||
		!getState().containers[containerId]?.initialized
	) {
		return;
	}

	if (
		getState().containers[containerId]?.isRefreshing ||
		getState().containers[containerId]?.numberOfRefreshBlocks > 0
	) {
		setState(
			updateContainerState(getState(), containerId, {
				scheduledRefresh: true,
			}),
		);
		return;
	}

	const isScheduredRefresh = getState().containers[containerId]?.scheduledRefresh;
	const lastRefreshViewsTime = Date.now();

	setState(
		updateContainerState(getState(), containerId, {
			isRefreshing: true,
			initialized: true,
			scheduledRefresh: false,
			refreshError: null,
		}),
	);

	try {
		const views = await navigationRemote.fetch(containerId);

		if (
			getState().containers[containerId]?.lastRefreshBlockTime > lastRefreshViewsTime ||
			getState().containers[containerId]?.numberOfRefreshBlocks > 0
		) {
			onSuccess?.('refreshViews.outdated');
			setState(
				updateContainerState(getState(), containerId, {
					isRefreshing: false,
					scheduledRefresh: true,
				}),
			);
		} else {
			if (isScheduredRefresh) {
				onSuccess?.('refreshViews.scheduled');
			} else {
				onSuccess?.('refreshViews');
			}

			setState(
				updateContainerState(getState(), containerId, {
					isRefreshing: false,
					refreshError: null,
					lastRefreshViewsTime: Date.now(),
					views: mergeViews(getState().containers[containerId].views, views),
				}),
			);
		}

		// next load if scheduled
		if (getState().containers[containerId]?.scheduledRefresh) {
			dispatch(refreshViews());
		}
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		setState(
			updateContainerState(getState(), containerId, {
				isRefreshing: false,
				refreshError: error,
			}),
		);

		onFailure?.(error, 'refreshViews');
	}
};

export const refreshViews = (): Action<State, Props> => async (storeApi, props) => {
	const { getState, setState } = storeApi;
	if (!getState().containers[props.containerId]?.controlledRefresh) {
		const controlledRefresh = throttle(
			debounce(refreshViewsFunc, debounceInterval),
			throttleInterval,
		);
		setState(
			updateContainerState(getState(), props.containerId, {
				controlledRefresh,
			}),
		);
	}
	await getState().containers[props.containerId]?.controlledRefresh?.(storeApi, props);
};
